
import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Button, Stack, Card, CardContent, CardActions,
  Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, FormControl, InputLabel, Grid,
  Tabs, Tab, Divider, List, ListItem, ListItemButton, ListItemText,
  Tooltip, InputAdornment, Collapse,
} from '@mui/material';
import {
  Add, Edit, Delete, Publish, Unpublished, CalendarMonth,
  FitnessCenter, ExpandMore, ExpandLess, DragIndicator,
  Lock, LockOpen, AttachMoney, Search, Close,
} from '@mui/icons-material';
import { CURRENCY_CODE, CURRENCY_SYMBOL, formatCurrency } from '../utils/currency';
import PageHeader from '../components/PageHeader';

// ─── Local exercise library (mirrors Flutter app) ────────────────────────────
const LOCAL_EXERCISES = [
  { id: '1',  name: 'Bench Press',      muscleGroup: 'Chest',     difficulty: 'Intermediate' },
  { id: '2',  name: 'Push-ups',         muscleGroup: 'Chest',     difficulty: 'Beginner' },
  { id: '3',  name: 'Dumbbell Flyes',   muscleGroup: 'Chest',     difficulty: 'Intermediate' },
  { id: '4',  name: 'Pull-ups',         muscleGroup: 'Back',      difficulty: 'Intermediate' },
  { id: '5',  name: 'Deadlift',         muscleGroup: 'Back',      difficulty: 'Advanced' },
  { id: '6',  name: 'Bent-over Rows',   muscleGroup: 'Back',      difficulty: 'Intermediate' },
  { id: '7',  name: 'Squats',           muscleGroup: 'Legs',      difficulty: 'Intermediate' },
  { id: '8',  name: 'Lunges',           muscleGroup: 'Legs',      difficulty: 'Beginner' },
  { id: '9',  name: 'Leg Press',        muscleGroup: 'Legs',      difficulty: 'Beginner' },
  { id: '10', name: 'Overhead Press',   muscleGroup: 'Shoulders', difficulty: 'Intermediate' },
  { id: '11', name: 'Lateral Raises',   muscleGroup: 'Shoulders', difficulty: 'Beginner' },
  { id: '12', name: 'Bicep Curls',      muscleGroup: 'Arms',      difficulty: 'Beginner' },
  { id: '13', name: 'Tricep Dips',      muscleGroup: 'Arms',      difficulty: 'Intermediate' },
  { id: '14', name: 'Plank',            muscleGroup: 'Core',      difficulty: 'Beginner' },
  { id: '15', name: 'Crunches',         muscleGroup: 'Core',      difficulty: 'Beginner' },
  { id: '16', name: 'Romanian Deadlift',muscleGroup: 'Legs',      difficulty: 'Intermediate' },
  { id: '17', name: 'Cable Rows',       muscleGroup: 'Back',      difficulty: 'Beginner' },
  { id: '18', name: 'Incline Press',    muscleGroup: 'Chest',     difficulty: 'Intermediate' },
  { id: '19', name: 'Hip Thrust',       muscleGroup: 'Legs',      difficulty: 'Beginner' },
  { id: '20', name: 'Face Pulls',       muscleGroup: 'Shoulders', difficulty: 'Beginner' },
];

const MUSCLE_GROUPS = ['All', ...new Set(LOCAL_EXERCISES.map(e => e.muscleGroup))];
const DAY_NAMES = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2);

const emptyPlan = () => ({
  id: uid(),
  name: '',
  description: '',
  type: 'free',       // 'free' | 'paid'
  price: '',
  level: 'beginner',  // 'beginner' | 'intermediate' | 'advanced'
  goal: 'stay_fit',
  durationWeeks: 4,
  published: false,
  days: Array.from({ length: 5 }, (_, i) => ({
    id: uid(),
    dayNumber: i + 1,
    dayName: DAY_NAMES[i],
    isRestDay: false,
    exercises: [],
  })),
});

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TrainerPlans() {
  const [plans, setPlans] = useState(() => {
    try { return JSON.parse(localStorage.getItem('trainerPlans') || '[]'); }
    catch { return []; }
  });
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // Persist to localStorage (replace with API call when backend ready)
  const save = useCallback((updated) => {
    setPlans(updated);
    localStorage.setItem('trainerPlans', JSON.stringify(updated));
  }, []);

  const openCreate = () => { setEditingPlan(emptyPlan()); setBuilderOpen(true); };
  const openEdit = (plan) => { setEditingPlan(JSON.parse(JSON.stringify(plan))); setBuilderOpen(true); };

  const handleSave = (plan) => {
    const exists = plans.find(p => p.id === plan.id);
    save(exists ? plans.map(p => p.id === plan.id ? plan : p) : [plan, ...plans]);
    setBuilderOpen(false);
  };

  const handleDelete = (id) => {
    save(plans.filter(p => p.id !== id));
    setDeleteId(null);
  };

  const togglePublish = (id) => {
    save(plans.map(p => p.id === id ? { ...p, published: !p.published } : p));
  };

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="Trainer Plans"
        subtitle="Create and publish workout plans — free or paid"
        gradient="linear-gradient(90deg, #D97706, #FBBF24)"
        actions={<Button variant="contained" size="small" startIcon={<Add />} onClick={openCreate}>Add Plan</Button>}
      />

      {/* Stats row */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: 'Total Plans', value: plans.length, color: 'primary.main' },
          { label: 'Published', value: plans.filter(p => p.published).length, color: 'success.main' },
          { label: 'Paid Plans', value: plans.filter(p => p.type === 'paid').length, color: 'warning.main' },
          { label: 'Free Plans', value: plans.filter(p => p.type === 'free').length, color: 'info.main' },
        ].map(s => (
          <Grid item xs={6} sm={3} key={s.label}>
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h5" fontWeight={700} color={s.color}>{s.value}</Typography>
                <Typography variant="body2" color="text.secondary">{s.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Plans grid */}
      {plans.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <CalendarMonth sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">No plans yet</Typography>
          <Button variant="contained" startIcon={<Add />} sx={{ mt: 2 }} onClick={openCreate}>
            Create First Plan
          </Button>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {plans.map(plan => (
            <Grid item xs={12} sm={6} lg={4} key={plan.id}>
              <PlanCard
                plan={plan}
                onEdit={() => openEdit(plan)}
                onDelete={() => setDeleteId(plan.id)}
                onTogglePublish={() => togglePublish(plan.id)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Plan Builder Dialog */}
      {builderOpen && editingPlan && (
        <PlanBuilderDialog
          plan={editingPlan}
          onSave={handleSave}
          onClose={() => setBuilderOpen(false)}
        />
      )}

      {/* Delete confirm */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Plan</DialogTitle>
        <DialogContent>
          <Typography>This will permanently delete the plan. Are you sure?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteId)}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Plan Card ────────────────────────────────────────────────────────────────
function PlanCard({ plan, onEdit, onDelete, onTogglePublish }) {
  const totalEx = plan.days.reduce((s, d) => s + d.exercises.length, 0);
  const activeDays = plan.days.filter(d => !d.isRestDay).length;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.3 }}>{plan.name}</Typography>
          <Stack direction="row" spacing={0.5}>
            <Chip
              size="small"
              icon={plan.type === 'paid' ? <AttachMoney sx={{ fontSize: 14 }} /> : <LockOpen sx={{ fontSize: 14 }} />}
              label={plan.type === 'paid' ? formatCurrency(plan.price) : 'Free'}
              color={plan.type === 'paid' ? 'warning' : 'success'}
            />
            <Chip
              size="small"
              icon={plan.published ? <Publish sx={{ fontSize: 14 }} /> : <Unpublished sx={{ fontSize: 14 }} />}
              label={plan.published ? 'Live' : 'Draft'}
              color={plan.published ? 'primary' : 'default'}
            />
          </Stack>
        </Stack>

        {plan.description && (
          <Typography variant="body2" color="text.secondary" mb={1.5} sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {plan.description}
          </Typography>
        )}

        <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5} mb={1.5}>
          <Chip size="small" label={plan.level} variant="outlined" />
          <Chip size="small" label={plan.goal?.replace('_', ' ')} variant="outlined" />
          <Chip size="small" label={`${plan.durationWeeks}w`} variant="outlined" />
        </Stack>

        <Divider sx={{ my: 1 }} />

        <Stack direction="row" spacing={2}>
          <Box textAlign="center">
            <Typography variant="h6" fontWeight={700}>{plan.days.length}</Typography>
            <Typography variant="caption" color="text.secondary">Days</Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" fontWeight={700}>{activeDays}</Typography>
            <Typography variant="caption" color="text.secondary">Active</Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" fontWeight={700}>{totalEx}</Typography>
            <Typography variant="caption" color="text.secondary">Exercises</Typography>
          </Box>
        </Stack>
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2, pt: 0, gap: 1 }}>
        <Button
          size="small"
          variant={plan.published ? 'outlined' : 'contained'}
          startIcon={plan.published ? <Unpublished /> : <Publish />}
          onClick={onTogglePublish}
          color={plan.published ? 'error' : 'primary'}
          sx={{ flex: 1 }}
        >
          {plan.published ? 'Unpublish' : 'Publish'}
        </Button>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={onEdit}><Edit fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={onDelete}><Delete fontSize="small" /></IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
}

// ─── Plan Builder Dialog ──────────────────────────────────────────────────────
function PlanBuilderDialog({ plan: initialPlan, onSave, onClose }) {
  const [plan, setPlan] = useState(initialPlan);
  const [activeDay, setActiveDay] = useState(0);
  const [tab, setTab] = useState(0); // 0=details, 1=schedule

  const updateMeta = (field, value) => setPlan(p => ({ ...p, [field]: value }));

  const updateDay = (dayId, changes) =>
    setPlan(p => ({ ...p, days: p.days.map(d => d.id === dayId ? { ...d, ...changes } : d) }));

  const addExercise = (dayId, ex) =>
    setPlan(p => ({
      ...p,
      days: p.days.map(d => d.id === dayId
        ? { ...d, exercises: [...d.exercises, { ...ex, id: uid(), sets: 3, reps: 12, weight: '' }] }
        : d),
    }));

  const updateExercise = (dayId, exId, changes) =>
    setPlan(p => ({
      ...p,
      days: p.days.map(d => d.id === dayId
        ? { ...d, exercises: d.exercises.map(e => e.id === exId ? { ...e, ...changes } : e) }
        : d),
    }));

  const removeExercise = (dayId, exId) =>
    setPlan(p => ({
      ...p,
      days: p.days.map(d => d.id === dayId
        ? { ...d, exercises: d.exercises.filter(e => e.id !== exId) }
        : d),
    }));

  const setNumDays = (n) => {
    const current = plan.days;
    if (n > current.length) {
      const extra = Array.from({ length: n - current.length }, (_, i) => ({
        id: uid(), dayNumber: current.length + i + 1,
        dayName: DAY_NAMES[(current.length + i) % 7],
        isRestDay: false, exercises: [],
      }));
      setPlan(p => ({ ...p, days: [...p.days, ...extra] }));
    } else {
      setPlan(p => ({ ...p, days: p.days.slice(0, n) }));
      if (activeDay >= n) setActiveDay(n - 1);
    }
  };

  const canSave = plan.name.trim().length > 0;

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose} PaperProps={{ sx: { height: '90vh' } }}>
      <DialogTitle sx={{ pb: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            {initialPlan.name ? `Edit: ${initialPlan.name}` : 'New Trainer Plan'}
          </Typography>
          <IconButton onClick={onClose} size="small"><Close /></IconButton>
        </Stack>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 1 }}>
          <Tab label="Plan Details" />
          <Tab label="Schedule & Exercises" />
        </Tabs>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* ── Tab 0: Details ── */}
        {tab === 0 && (
          <Box sx={{ p: 3, overflowY: 'auto' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Plan Name *" value={plan.name}
                  onChange={e => updateMeta('name', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={2} label="Description"
                  value={plan.description} onChange={e => updateMeta('description', e.target.value)} />
              </Grid>

              {/* Type + Price */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select value={plan.type} label="Type" onChange={e => updateMeta('type', e.target.value)}>
                    <MenuItem value="free"><Stack direction="row" spacing={1} alignItems="center"><LockOpen fontSize="small" /><span>Free</span></Stack></MenuItem>
                    <MenuItem value="paid"><Stack direction="row" spacing={1} alignItems="center"><Lock fontSize="small" /><span>Paid</span></Stack></MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {plan.type === 'paid' && (
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label={`Price (${CURRENCY_CODE})`} type="number"
                    value={plan.price} onChange={e => updateMeta('price', e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment> }} />
                </Grid>
              )}

              {/* Level + Goal */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Fitness Level</InputLabel>
                  <Select value={plan.level} label="Fitness Level" onChange={e => updateMeta('level', e.target.value)}>
                    <MenuItem value="beginner">Beginner</MenuItem>
                    <MenuItem value="intermediate">Intermediate</MenuItem>
                    <MenuItem value="advanced">Advanced</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Goal</InputLabel>
                  <Select value={plan.goal} label="Goal" onChange={e => updateMeta('goal', e.target.value)}>
                    <MenuItem value="lose_weight">Lose Weight</MenuItem>
                    <MenuItem value="build_muscle">Build Muscle</MenuItem>
                    <MenuItem value="stay_fit">Stay Fit</MenuItem>
                    <MenuItem value="improve_endurance">Improve Endurance</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Duration + Days */}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Duration (weeks)" type="number"
                  value={plan.durationWeeks} onChange={e => updateMeta('durationWeeks', parseInt(e.target.value) || 1)}
                  inputProps={{ min: 1, max: 52 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" mb={1}>Days per week</Typography>
                <Stack direction="row" spacing={1}>
                  {[1,2,3,4,5,6,7].map(n => (
                    <Button key={n} size="small" variant={plan.days.length === n ? 'contained' : 'outlined'}
                      onClick={() => setNumDays(n)} sx={{ minWidth: 36, px: 0 }}>
                      {n}
                    </Button>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* ── Tab 1: Schedule ── */}
        {tab === 1 && (
          <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
            {/* Day list sidebar */}
            <Box sx={{ width: 160, borderRight: 1, borderColor: 'divider', overflowY: 'auto', flexShrink: 0 }}>
              {plan.days.map((day, i) => (
                <Box key={day.id}
                  onClick={() => setActiveDay(i)}
                  sx={{
                    px: 2, py: 1.5, cursor: 'pointer', borderBottom: 1, borderColor: 'divider',
                    bgcolor: activeDay === i ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}>
                  <Typography variant="caption" color="text.secondary">Day {day.dayNumber}</Typography>
                  <Typography variant="body2" fontWeight={activeDay === i ? 600 : 400} noWrap>
                    {day.isRestDay ? '😴 Rest' : day.dayName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {day.isRestDay ? '' : `${day.exercises.length} ex`}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Day editor */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
              {plan.days[activeDay] && (
                <DayEditor
                  day={plan.days[activeDay]}
                  onUpdate={(changes) => updateDay(plan.days[activeDay].id, changes)}
                  onAddExercise={(ex) => addExercise(plan.days[activeDay].id, ex)}
                  onUpdateExercise={(exId, changes) => updateExercise(plan.days[activeDay].id, exId, changes)}
                  onRemoveExercise={(exId) => removeExercise(plan.days[activeDay].id, exId)}
                />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="outlined" onClick={() => onSave({ ...plan, published: false })} disabled={!canSave}>
          Save as Draft
        </Button>
        <Button variant="contained" onClick={() => onSave({ ...plan, published: true })} disabled={!canSave}
          startIcon={<Publish />}>
          Save & Publish
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Day Editor ───────────────────────────────────────────────────────────────
function DayEditor({ day, onUpdate, onAddExercise, onUpdateExercise, onRemoveExercise }) {
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <Box>
      {/* Day header controls */}
      <Stack direction="row" spacing={2} alignItems="center" mb={2} flexWrap="wrap">
        <TextField size="small" label="Day Name" value={day.dayName}
          onChange={e => onUpdate({ dayName: e.target.value })} sx={{ width: 200 }} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">Rest Day</Typography>
          <Button size="small"
            variant={day.isRestDay ? 'contained' : 'outlined'}
            color={day.isRestDay ? 'warning' : 'inherit'}
            onClick={() => onUpdate({ isRestDay: !day.isRestDay })}>
            {day.isRestDay ? '😴 Yes' : 'No'}
          </Button>
        </Stack>
      </Stack>

      {day.isRestDay ? (
        <Box textAlign="center" py={4}>
          <Typography variant="h2">😴</Typography>
          <Typography color="text.secondary">Rest & Recovery Day</Typography>
        </Box>
      ) : (
        <>
          {/* Exercise list */}
          {day.exercises.length === 0 ? (
            <Box textAlign="center" py={4} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, borderStyle: 'dashed' }}>
              <FitnessCenter sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary" mb={2}>No exercises yet</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => setPickerOpen(true)}>
                Add Exercise
              </Button>
            </Box>
          ) : (
            <>
              <List disablePadding>
                {day.exercises.map((ex, i) => (
                  <ExerciseRow key={ex.id} exercise={ex} index={i}
                    onUpdate={(changes) => onUpdateExercise(ex.id, changes)}
                    onRemove={() => onRemoveExercise(ex.id)} />
                ))}
              </List>
              <Button startIcon={<Add />} onClick={() => setPickerOpen(true)} sx={{ mt: 1 }}>
                Add Exercise
              </Button>
            </>
          )}
        </>
      )}

      {/* Exercise picker */}
      {pickerOpen && (
        <ExercisePicker
          onSelect={(ex) => { onAddExercise(ex); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </Box>
  );
}

// ─── Exercise Row ─────────────────────────────────────────────────────────────
function ExerciseRow({ exercise, index, onUpdate, onRemove }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card variant="outlined" sx={{ mb: 1 }}>
      <ListItem
        secondaryAction={
          <Stack direction="row" spacing={0.5}>
            <IconButton size="small" onClick={() => setExpanded(e => !e)}>
              {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
            <IconButton size="small" color="error" onClick={onRemove}>
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        }
      >
        <DragIndicator sx={{ color: 'text.disabled', mr: 1, fontSize: 18 }} />
        <ListItemText
          primary={<Typography variant="body2" fontWeight={600}>{exercise.exerciseName}</Typography>}
          secondary={
            <Stack direction="row" spacing={1} mt={0.5}>
              <Chip size="small" label={exercise.muscleGroup} variant="outlined" />
              <Typography variant="caption" color="text.secondary">
                {exercise.sets} × {exercise.reps} reps
                {exercise.weight ? ` @ ${exercise.weight}kg` : ''}
              </Typography>
            </Stack>
          }
        />
      </ListItem>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField size="small" fullWidth label="Sets" type="number"
                value={exercise.sets} onChange={e => onUpdate({ sets: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1, max: 20 }} />
            </Grid>
            <Grid item xs={4}>
              <TextField size="small" fullWidth label="Reps" type="number"
                value={exercise.reps} onChange={e => onUpdate({ reps: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1, max: 100 }} />
            </Grid>
            <Grid item xs={4}>
              <TextField size="small" fullWidth label="Weight (kg)" type="number"
                value={exercise.weight || ''} onChange={e => onUpdate({ weight: e.target.value })}
                placeholder="Optional" />
            </Grid>
            <Grid item xs={12}>
              <TextField size="small" fullWidth label="Notes" value={exercise.notes || ''}
                onChange={e => onUpdate({ notes: e.target.value })} placeholder="e.g. slow eccentric" />
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Card>
  );
}

// ─── Exercise Picker ──────────────────────────────────────────────────────────
function ExercisePicker({ onSelect, onClose }) {
  const [query, setQuery] = useState('');
  const [muscle, setMuscle] = useState('All');

  const filtered = LOCAL_EXERCISES.filter(e => {
    const matchMuscle = muscle === 'All' || e.muscleGroup === muscle;
    const matchQuery = !query || e.name.toLowerCase().includes(query.toLowerCase());
    return matchMuscle && matchQuery;
  });

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontWeight={600}>Add Exercise</Typography>
          <IconButton size="small" onClick={onClose}><Close /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <TextField size="small" fullWidth placeholder="Search exercises..."
            value={query} onChange={e => setQuery(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />

          <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
            {MUSCLE_GROUPS.map(m => (
              <Chip key={m} size="small" label={m} clickable
                color={muscle === m ? 'primary' : 'default'}
                variant={muscle === m ? 'filled' : 'outlined'}
                onClick={() => setMuscle(m)} />
            ))}
          </Stack>

          <List dense disablePadding>
            {filtered.map(ex => (
              <ListItem key={ex.id} disablePadding>
                <ListItemButton onClick={() => onSelect({ exerciseId: ex.id, exerciseName: ex.name, muscleGroup: ex.muscleGroup })}
                  sx={{ borderRadius: 1, mb: 0.5 }}>
                  <ListItemText
                    primary={ex.name}
                    secondary={
                      <Stack direction="row" spacing={1} mt={0.25}>
                        <Chip size="small" label={ex.muscleGroup} variant="outlined" />
                        <Chip size="small" label={ex.difficulty} variant="outlined" />
                      </Stack>
                    }
                  />
                  <Add fontSize="small" color="primary" />
                </ListItemButton>
              </ListItem>
            ))}
            {filtered.length === 0 && (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                No exercises found
              </Typography>
            )}
          </List>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
